import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, X, Loader2 } from 'lucide-react';
import { useImpersonator, useAuthStore } from '../state_management_and_stores/auth.store';
import { useStopImpersonating } from '../api/extensions/useAdminOps';
import { authService } from '../services/auth.service';
import { resetClientState } from '../lib/resetClientState';
import { useToast } from '../contexts/ToastContext';

/**
 * Fixed banner shown only when the active session is an impersonation. Click
 * "Return to admin" to swap the session_id cookie back to the original admin
 * session and rehydrate the auth store.
 *
 * The banner sits above all routed content so admins always have a way out,
 * regardless of which page the impersonated user lands on. The component
 * mounts unconditionally (App-level), but renders nothing when impersonator
 * is null — keeps the App tree simple.
 */
export function ImpersonationBanner() {
  const impersonator = useImpersonator();
  const stopImpersonating = useStopImpersonating();
  const setAuthData = useAuthStore((s) => s.setAuthData);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [isStopping, setIsStopping] = useState(false);

  if (!impersonator?.isImpersonating) return null;

  const handleStop = async () => {
    if (isStopping) return;
    setIsStopping(true);
    try {
      await stopImpersonating.mutateAsync();
      // Cookie has flipped back to the admin session. Wipe React Query cache
      // so impersonated user's queries don't bleed into the admin context,
      // then rehydrate the auth store from the new session.
      resetClientState(queryClient);
      const session = await authService.validateSession();
      setAuthData({
        user: session.user,
        profile: session.profile,
        role: session.role,
        permissions: session.permissions,
        impersonator: session.impersonator ?? null,
      });
      showSuccess('Returned to your admin account');
      // Bring the admin back to a sensible default. Their previous in-flight
      // navigation almost certainly was a page the impersonated user could
      // reach but the admin has no business being on now.
      navigate('/users', { replace: true });
    } catch (err: any) {
      showError(
        'Failed to stop impersonating',
        err?.message || 'Try refreshing the page or logging in again.',
      );
    } finally {
      setIsStopping(false);
    }
  };

  const adminLabel =
    impersonator.impersonatorDisplayName ||
    impersonator.impersonatorEmail ||
    'admin account';

  return (
    <div
      role="status"
      aria-live="polite"
      // Sticky (not fixed) so it pushes content down rather than overlapping
      // the app header. high z-index to sit above dropdowns / modals if the
      // impersonated user opens one.
      className="sticky top-0 z-[60] w-full border-b border-amber-500/40 bg-amber-500/10 backdrop-blur supports-[backdrop-filter]:bg-amber-500/20"
    >
      {/* Full-bleed: no max-width wrapper. The banner is a global "you're
          not yourself" indicator and should run edge-to-edge so it reads
          as part of the chrome rather than page content. */}
      <div className="flex w-full items-center justify-between gap-3 px-4 py-2 text-sm sm:px-6">
        <div className="flex min-w-0 items-center gap-2 text-amber-900 dark:text-amber-200">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <span className="truncate">
            <span className="font-semibold">Impersonating user</span>
            <span className="hidden sm:inline"> — anything you do is recorded as them. </span>
            <span className="hidden md:inline">Return to <span className="font-medium">{adminLabel}</span> when finished.</span>
          </span>
        </div>
        <button
          type="button"
          onClick={handleStop}
          disabled={isStopping}
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-600 bg-amber-600 px-3 py-1 text-xs font-medium text-white shadow-sm transition-colors hover:bg-amber-700 disabled:opacity-60"
        >
          {isStopping ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <X className="h-3.5 w-3.5" />
          )}
          Return to admin
        </button>
      </div>
    </div>
  );
}

export default ImpersonationBanner;
